/**
 * Task 151-B — the packages cards grid: the primary `/app/services`
 * surface. Renders one `PackageCard` per package plus a "create
 * package" entry (managers only), and owns the create/edit drawer's
 * open state. All persistence happens in `PackageDrawer`; this
 * component just refreshes on save.
 *
 * Archive support: managers can soft-archive (is_active=false) an active
 * package or restore an archived one via in-card actions; the active grid
 * stays as today and archived packages render under a clearly-separated
 * "المؤرشفة" section below. This is the team's intended soft-delete (see
 * the agency_services_packages migration's "No DELETE policies anywhere"
 * design note); there is NO hard-delete path. Existing client assignments
 * are unaffected (their `services_snapshot` is immutable).
 */

import { useState } from 'react';
import { upsertAgencyPackage } from '../../api/endpoints';
import type { AgencyPackageRow, AgencyServiceRow } from '../../api/types';
import { EmptyState } from '../states';
import { PackageCard } from './PackageCard';
import { PackageDrawer } from './PackageDrawer';
import { buildArchiveInput, buildUnarchiveInput, partitionPackages } from './packageArchiveModel';

type DrawerState = { readonly pkg: AgencyPackageRow | null } | null;

/** Manager-initiated archive/unarchive awaiting an explicit confirm. */
type PendingArchive = {
  readonly pkg: AgencyPackageRow;
  readonly action: 'archive' | 'unarchive';
} | null;

export function PackagesGrid({
  services,
  packages,
  canManage,
  onSaved,
}: {
  readonly services: ReadonlyArray<AgencyServiceRow>;
  readonly packages: ReadonlyArray<AgencyPackageRow>;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [pending, setPending] = useState<PendingArchive>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { active, archived } = partitionPackages(packages);

  async function runPending() {
    if (!pending) return;
    setSubmitting(true);
    setError(null);
    try {
      const input =
        pending.action === 'archive'
          ? buildArchiveInput(pending.pkg)
          : buildUnarchiveInput(pending.pkg);
      await upsertAgencyPackage({ input });
      setSubmitting(false);
      setPending(null);
      onSaved();
    } catch {
      setSubmitting(false);
      setPending(null);
      setError('تعذّر تنفيذ العملية. حاول مجدداً.');
    }
  }

  return (
    // Not a `.card`: the grid frames each package as its own card, so a
    // card wrapper here would nest cards inside a card.
    <section>
      <div className="sec-title">
        <h2>
          إدارة <em className="serif italic">الباقات</em>
        </h2>
        <span className="meta">{active.length} باقة</span>
      </div>
      <p className="tiny" style={{ color: 'var(--muted)', marginTop: 4, marginBottom: 16 }}>
        عرّف الباقات التي تقدّمها لعملائك. كل باقة تجمع خدمات وإعداداتها التفصيلية.
      </p>

      {pending ? (
        <div
          className="banner banner-info"
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 14,
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            {pending.action === 'archive'
              ? `هل تريد أرشفة الباقة «${pending.pkg.name}»؟ الباقات المعيّنة للعملاء الحاليين تبقى كما هي (لقطة الباقة محفوظة)؛ لن تظهر للتعيين لعملاء جدد.`
              : `هل تريد استعادة الباقة «${pending.pkg.name}» إلى الكتالوج النشط؟`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="auth-cta"
              onClick={runPending}
              disabled={submitting}
              style={{ width: 'auto' }}
            >
              {submitting ? <span className="spinner" /> : null}
              <span>تأكيد</span>
            </button>
            <button
              type="button"
              className="btn-text"
              onClick={() => setPending(null)}
              disabled={submitting}
              style={{ padding: '10px 14px' }}
            >
              إلغاء
            </button>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className="banner banner-error" role="alert" style={{ marginBottom: 14 }}>
          {error}
        </div>
      ) : null}

      {active.length === 0 && !canManage ? (
        <EmptyState title="لا توجد باقات بعد" description="لم يتم تعريف أي باقة خدمة حتى الآن." />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
            gap: 16,
          }}
        >
          {active.map((pkg) => (
            <PackageCard
              key={pkg.id}
              pkg={pkg}
              canManage={canManage}
              onEdit={() => setDrawer({ pkg })}
              onArchive={canManage ? () => setPending({ pkg, action: 'archive' }) : undefined}
            />
          ))}

          {canManage ? (
            <button
              type="button"
              onClick={() => setDrawer({ pkg: null })}
              style={{
                border: '2px dashed var(--line-3)',
                borderRadius: 'var(--r-lg)',
                padding: 32,
                background: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                color: 'var(--muted)',
                cursor: 'pointer',
                minHeight: 220,
              }}
            >
              <span style={{ fontSize: 26, lineHeight: 1 }}>+</span>
              <span style={{ fontSize: 16, fontWeight: 600 }}>إنشاء باقة جديدة</span>
              <span className="tiny">ابدأ من باقة فارغة وأضِف الخدمات وإعداداتها</span>
            </button>
          ) : null}
        </div>
      )}

      {/* Task 166 — archived packages section is COLLAPSED BY DEFAULT inside a
          native `<details>`. The previous always-visible section was visually
          dominating the active grid + the "إنشاء باقة جديدة" entry, which
          should remain the primary focus. When the org has zero archived
          packages we render nothing (same as today). When opened the user
          sees the existing archived grid + restore actions verbatim. */}
      {archived.length > 0 ? (
        <details style={{ marginTop: 28 }}>
          <summary
            style={{
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--muted)',
              padding: '8px 0',
              userSelect: 'none',
            }}
          >
            المؤرشفة ({archived.length})
          </summary>
          <div style={{ marginTop: 12 }}>
            <p className="tiny" style={{ color: 'var(--muted)', marginTop: 0, marginBottom: 14 }}>
              الباقات المؤرشفة لا تظهر للتعيين لعملاء جدد. الباقات المعيّنة سابقاً للعملاء الحاليين
              تبقى كما هي (لقطة الباقة محفوظة).
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: 16,
              }}
            >
              {archived.map((pkg) => (
                <PackageCard
                  key={pkg.id}
                  pkg={pkg}
                  canManage={canManage}
                  onEdit={() => setDrawer({ pkg })}
                  onUnarchive={
                    canManage ? () => setPending({ pkg, action: 'unarchive' }) : undefined
                  }
                />
              ))}
            </div>
          </div>
        </details>
      ) : null}

      {drawer ? (
        <PackageDrawer
          pkg={drawer.pkg}
          services={services}
          onClose={() => setDrawer(null)}
          onSaved={() => {
            setDrawer(null);
            onSaved();
          }}
        />
      ) : null}
    </section>
  );
}
